/**
 * Schedule page — daily calendar view for front-desk planners.
 *
 * Day view (default):
 *   - Grooming-capacity indicator (X of Y slots filled)
 *   - Staff-coverage strip — each staffer's shift positioned over the
 *     day's time-grid (8am–6pm by default)
 *   - Time-grid calendar — grooming appointments rendered as
 *     time-blocks whose height = actual service duration (so a 90-min
 *     full groom looks 1.5× the height of a 60-min bath-and-dry)
 *   - Arrivals + departures (daycare / boarding) below the calendar
 *
 * Week view (toggle):
 *   - Legacy weekly grid showing every shift across Mon–Sun
 *
 * #665 — the Add-shift form is shared by BOTH views. It originally
 * rendered only inside the Week branch, which made Day view — the
 * default landing — a dead end: capacity + "No staff scheduled today."
 * with no affordance and no pointer. The owner concluded shift creation
 * didn't exist, and prod had zero schedules ever entered. Day view now
 * carries its own "Add shift" action (toolbar + actionable empty state,
 * Principle 2: the next action is always obvious; five mandatory
 * component states: empty = teaching copy + action, never a dead
 * sentence), and a successful save from Day view reloads the day so the
 * new shift shows up in the coverage strip it was created from.
 *
 * Composes existing server surfaces — no new server endpoints
 * required for this issue:
 *   - GET /api/admin/dashboard/staff-on-duty?date=  → shifts + role
 *   - GET /api/admin/dashboard/today?date=          → arrivals/departures
 *   - GET /api/bookings/admin/schedule?...          → grooming bookings
 *   - GET /api/bookings/admin/service-types         → duration_minutes
 *   - GET /api/admin/dashboard/facility-status?date= → per-service
 *     capacity from booking-engine rules (#283 / Lane E2)
 *
 * File ownership scope (issue #30):
 *   - apps/admin-app/src/pages/Schedule.tsx
 *   - apps/admin-app/src/components/schedule/CalendarGrid.tsx (new)
 *   - apps/admin-app/src/components/schedule/StaffShift.tsx (new)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClientConfig } from '@ferro/config';
import {
  addIsoDays,
  adminStaffApi,
  copyStaffWeek,
  createStaffShift,
  deleteStaffShift,
  fetchAdminSchedule,
  fetchScheduleDayArrivals,
  fetchScheduleDayShifts,
  fetchScheduleGroomingCapacity,
  fetchServiceTypes,
  fetchStaffWeek,
  fetchTimeOff,
  humanizeApiError,
  mondayIsoDate,
  publishStaffSchedule,
  readSession,
  ShiftConflictError,
  STAFF_ROLES,
  STAFF_ROLE_LABELS,
  TIME_OFF_TYPE_LABELS,
  todayIsoDate,
  type AdminBooking,
  type AdminStaffUser,
  type CopyWeekSkip,
  type PublishNotifyMode,
  type ScheduleDayArrival,
  type ScheduleDayShift,
  type ScheduleServiceCapacity,
  type ServiceTypeSummary,
  type StaffRole,
  type StaffShift,
  type TimeOffRequest,
  type TimeOffType,
} from '../lib/api.js';
import {
  CalendarGrid,
  type GroomingBlock,
} from '../components/schedule/CalendarGrid.js';
import { StaffShiftRow } from '../components/schedule/StaffShift.js';
import { countVisits, type ServiceLegRow } from '../components/dashboard/service-legs.js';
import {
  jobFunctionLabel,
} from '../components/schedule/JobFunctionChip.js';
import {
  isDraftShift,
  WeekGrid,
  type WeekGridPerson,
} from '../components/schedule/WeekGrid.js';
import { ShiftEditSheet } from '../components/schedule/ShiftEditSheet.js';
import { PublishModal } from '../components/schedule/PublishModal.js';
import { TimeOffQueue } from '../components/timeoff/TimeOffQueue.js';
import { TimeOffRequestForm } from '../components/timeoff/TimeOffRequestForm.js';
import { useAuth } from '../contexts/AuthContext.js';
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';
import { formatDayRange, formatTimeRange, isDayWithin } from '../lib/schedule-dates.js';

type ScheduleProps = {
  config: ClientConfig;
};

type ScheduleView = 'day' | 'week';

/**
 * Ted #23 (#984) — a `/today` row as the day view reads it. An added service
 * (nail trim, bath) is its own row on that feed; `ScheduleDayArrival` predates
 * the leg facts the same endpoint now carries. Optional for deploy skew:
 * absent reads as an ordinary booking, which is the pre-#984 list.
 */
type ScheduleDayRow = ScheduleDayArrival &
  Pick<ServiceLegRow, 'isLeg' | 'parentBookingId' | 'addOnName'>;

type DayState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      shifts: ScheduleDayShift[];
      arrivals: ScheduleDayRow[];
      departures: ScheduleDayRow[];
      bookings: AdminBooking[];
      serviceTypes: ServiceTypeSummary[];
      /** #283 — rule-resolved grooming capacity from facility-status.
       *  `null` = the capacity read failed or returned nothing usable;
       *  the indicator falls back to DEFAULT_GROOMING_SLOTS and MUST
       *  label itself "(default)" (Principle 5: never lie about data). */
      groomingCapacity: ScheduleServiceCapacity | null;
    };

type WeekState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; shifts: StaffShift[] };

// #278 — Roster is a first-class state machine. A failed roster fetch must
// surface a clear error + a retry affordance, NEVER degrade to a raw-UUID
// text input (a front-desk planner must never hand-type a staff UUID).
// `message: null` means "no server-provided detail" — the render layer
// substitutes the translated fallback copy at display time so the error
// text follows the user's current language.
type RosterState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string | null }
  | { kind: 'ready'; staff: AdminStaffUser[] };

/**
 * #427 W2 — APPROVED time off, used to draw "who is away" bands on the grid
 * so a planner sees the absence BEFORE scheduling over it.
 *
 * `unavailable` is a first-class honest state (Principle 5): if the time-off
 * read fails we must not let the resulting absence of bands read as "nobody
 * is away" — the grid says so out loud instead.
 */
type TimeOffState =
  | { kind: 'ok'; requests: TimeOffRequest[] }
  | { kind: 'unavailable' };

/**
 * #427 W2 — result of "Copy last week". A partial copy is NOT a success:
 * `skipped` people are named, with the reason, in the result banner
 * (Principle 5 — a partial success must never render as a clean success).
 */
type CopyState =
  | { kind: 'idle' }
  | { kind: 'copying' }
  | { kind: 'done'; created: number; skipped: CopyWeekSkip[] }
  | { kind: 'error'; message: string };

/** #427 W2 — a 409 the planner can act on, held next to the Add-shift form. */
type ShiftConflictState =
  /** Physics: you cannot be in two places at once. No override is offered. */
  | { kind: 'overlapping_shift'; message: string }
  /** Policy: the manager MAY knowingly schedule over approved time off. */
  | { kind: 'time_off_conflict'; message: string };

function timeOffTypeLabel(type: string): string {
  return TIME_OFF_TYPE_LABELS[type as TimeOffType] ?? 'Time off';
}

/** "time off" / "already scheduled" — never the raw skip enum. */
function skipReasonLabel(reason: CopyWeekSkip['reason']): string {
  return reason === 'time_off' ? 'time off' : 'already scheduled';
}

/**
 * "Dezirea (time off) and Jorge (already scheduled)" — the people a copy
 * skipped, by NAME, with the reason. Deduped: the server can skip the same
 * person for several shifts, and a planner needs the person, not the row count.
 */
function formatSkips(skipped: ReadonlyArray<CopyWeekSkip>): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const s of skipped) {
    const key = `${s.staff_user_id}:${s.reason}`;
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(`${s.staff_name} (${skipReasonLabel(s.reason)})`);
  }
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DAY_START_HOUR = 8;
const DAY_END_HOUR = 18;
const DEFAULT_GROOMING_DURATION_MIN = 60;
// #283 — LAST-RESORT fallback only. The real denominator now comes from
// GET /api/admin/dashboard/facility-status → services.grooming.capacity
// (rule-resolved by the booking engine, Lane A2 2026-07-09). This
// constant is used only when the tenant has no grooming capacity rules
// or the capacity read fails, and the indicator then labels itself
// "(default)" — a defaulted denominator is never dressed up as
// configured truth (DESIGN-PHILOSOPHY Principle 5). The value 6 is
// HTHD ballpark provided by the facility owner on 2026-06-12.
const DEFAULT_GROOMING_SLOTS = 6;

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':');
  const hours = Number(h);
  const mins = Number(m ?? '0');
  if (Number.isNaN(hours) || Number.isNaN(mins)) return NaN;
  return hours * 60 + mins;
}

/**
 * #665 — the coverage strip's window derives from the day's actual shifts
 * (whole-hour bounds), replacing the hardcoded 8:00–18:00 clamp that
 * rendered an evening/overnight-function shift as a zero-width sliver
 * pinned to the right edge (P5: a clipped bar lies about the hours).
 * No shifts (or unparseable times) → the historical default window.
 */
function coverageWindow(
  shifts: ReadonlyArray<ScheduleDayShift>,
): { startHour: number; endHour: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const s of shifts) {
    const start = hhmmToMinutes(s.shiftStart);
    const end = hhmmToMinutes(s.shiftEnd);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    min = Math.min(min, start);
    max = Math.max(max, end);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { startHour: DAY_START_HOUR, endHour: DAY_END_HOUR };
  }
  const startHour = Math.max(0, Math.floor(min / 60));
  const endHour = Math.min(24, Math.ceil(max / 60));
  if (endHour <= startHour) {
    return { startHour: DAY_START_HOUR, endHour: DAY_END_HOUR };
  }
  return { startHour, endHour };
}

function formatLongDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatExpectedTime(raw: string | null): string {
  if (!raw) return '';
  if (/^\d{1,2}:\d{2}$/.test(raw)) return raw;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return raw;
  }
}

function capacityBarClass(filled: number, total: number): string {
  if (total <= 0) return '';
  const pct = (filled / total) * 100;
  if (pct >= 100) return 'ferro-schedule-capacity__bar--full';
  if (pct >= 80) return 'ferro-schedule-capacity__bar--warning';
  return '';
}

// #278 — Human label for a roster entry, used as the <option> text in the
// Add-shift staff picker. Name first (the planner thinks in names), with
// the username as a disambiguator for same-name staff. Never the UUID.
function staffLabel(s: AdminStaffUser): string {
  const name = `${s.first_name} ${s.last_name}`.trim();
  return name ? `${name} (${s.username})` : s.username;
}

export function Schedule({ config }: ScheduleProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const { t } = useTranslation();
  const { staff } = useAuth();
  const [view, setView] = useState<ScheduleView>('day');

  // #427 W2 — owner/manager run the approve/deny queue; every other role
  // still gets the "ask for a day off" form below it.
  const canManageTimeOff = rolesHavePermission(
    effectiveRolesOf(staff),
    'time-off:manage:all',
  );

  // #712 — write affordances mirror the SERVER's write gate. Every schedule
  // write route (POST /, DELETE /:id, POST /copy-week — see
  // route-permission-map.ts) requires `staff-schedule:write:all`, but the
  // /schedule route guard deliberately admits receptionists, who hold only
  // `staff-schedule:read:all`. Before this gate a receptionist saw Add shift
  // (both views + the empty-state CTA), Copy last week and per-shift Remove —
  // and every click ended in a 403. Read-only roles keep the FULL read
  // experience (capacity, coverage, week grid, time-off bands, their own
  // time-off ask); they just never see a button the server will refuse
  // (Principle 5: honest surfaces — the #290 leak class at button level).
  const canWriteSchedule = rolesHavePermission(
    effectiveRolesOf(staff),
    'staff-schedule:write:all',
  );

  // Day-view state ─────────────────────────────────────────────────────
  const [date, setDate] = useState<string>(() => todayIsoDate());
  const [dayState, setDayState] = useState<DayState>({ kind: 'loading' });

  const loadDay = useCallback(async () => {
    setDayState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setDayState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const [shiftsResp, arrivalsResp, bookings, serviceTypes, capacityResp] =
        await Promise.all([
          fetchScheduleDayShifts(apiUrl, token, date),
          fetchScheduleDayArrivals(apiUrl, token, date),
          fetchAdminSchedule(apiUrl, token, {
            startDate: date,
            endDate: date,
          }),
          fetchServiceTypes(apiUrl, token),
          // #283 — capacity is an enhancement, not a gate: if this read
          // fails the day view still renders and the grooming indicator
          // falls back to the "(default)"-labelled constant instead of
          // taking the whole page down with it.
          fetchScheduleGroomingCapacity(apiUrl, token, date).catch(() => null),
        ]);
      setDayState({
        kind: 'ready',
        shifts: shiftsResp.shifts ?? [],
        arrivals: arrivalsResp.arrivals ?? [],
        departures: arrivalsResp.departures ?? [],
        bookings: bookings ?? [],
        serviceTypes: serviceTypes ?? [],
        groomingCapacity: capacityResp?.services?.grooming ?? null,
      });
    } catch (err) {
      const message = humanizeApiError(err instanceof Error ? err.message : null);
      setDayState({ kind: 'error', message });
    }
  }, [apiUrl, date]);

  // Week-view state ────────────────────────────────────────────────────
  const [weekStart, setWeekStart] = useState<string>(() => mondayIsoDate());
  const [weekState, setWeekState] = useState<WeekState>({ kind: 'loading' });
  const [showForm, setShowForm] = useState(false);
  const [formDate, setFormDate] = useState(weekStart);
  const [formStaffId, setFormStaffId] = useState('');
  const [formStart, setFormStart] = useState('08:00');
  const [formEnd, setFormEnd] = useState('17:00');
  const [formRole, setFormRole] = useState<StaffRole>('general');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // #278 — Staff roster for the Add-shift picker, as a state machine. A
  // failed fetch surfaces a clear error + a "Retry roster" affordance and
  // NEVER falls back to a raw-UUID text input.
  const [rosterState, setRosterState] = useState<RosterState>({ kind: 'idle' });

  // #427 W2 — approved time off (grid bands), copy-week result, and the
  // recoverable 409 from Add shift.
  const [timeOffState, setTimeOffState] = useState<TimeOffState>({
    kind: 'ok',
    requests: [],
  });
  const [copyState, setCopyState] = useState<CopyState>({ kind: 'idle' });
  const [conflict, setConflict] = useState<ShiftConflictState | null>(null);
  const [showTimeOffForm, setShowTimeOffForm] = useState(false);

  // ── #665 PR-B — master-grid state ──────────────────────────────────
  // Layer lens: null = All. The chip SET is derived from the last
  // UNFILTERED load (deriving it from a filtered response would collapse
  // the chip row to the active lane and strand the lens).
  const [jobFilter, setJobFilter] = useState<StaffRole | null>(null);
  // The last known WEEK-WIDE (unfiltered) shift set, kept in sync by the
  // in-place create/edit/delete handlers. Publish acts on the whole week,
  // so the Publish button, its draft count, the modal headline AND the
  // lane chips must all derive from THIS — never from the lane-filtered
  // `weekState` (review fix: a filtered week undercounted, and could claim
  // "No draft shifts" while drafts sat outside the lane). null = no
  // unfiltered load has succeeded yet.
  const [weekWideShifts, setWeekWideShifts] = useState<StaffShift[] | null>(
    null,
  );
  // Chip → edit sheet (times, job function, notes, PERSON, delete).
  const [editShift, setEditShift] = useState<StaffShift | null>(null);
  // Draft→publish commitment point. The {from,to} range and the headline
  // count are FROZEN at modal-open (review fix: without the freeze,
  // tab-out → change week → confirm published a different week than the
  // modal named).
  const [publishTarget, setPublishTarget] = useState<{
    from: string;
    to: string;
    draftCount: number;
  } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<{
    published: number;
    notified: number;
  } | null>(null);

  const loadTimeOff = useCallback(async () => {
    const { token } = readSession();
    if (!token) return;
    try {
      const { requests } = await fetchTimeOff(apiUrl, token, {
        status: 'approved',
      });
      setTimeOffState({ kind: 'ok', requests: requests ?? [] });
    } catch {
      // Honest degradation (P5): we say we don't know who's away rather than
      // rendering a grid that silently implies nobody is.
      setTimeOffState({ kind: 'unavailable' });
    }
  }, [apiUrl]);

  const loadWeek = useCallback(async () => {
    setWeekState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setWeekState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      // #665 PR-B — the layer lens is a SERVER-side filter (?jobFunction=).
      // When a lane is active we ALSO refresh the unfiltered week in
      // parallel: Publish speaks for the WHOLE week, so its count (and the
      // lane chips) must track the current week even while a lens is on —
      // otherwise navigating weeks under a lens leaves them describing the
      // previous week (review fix).
      const [data, unfiltered] = await Promise.all([
        fetchStaffWeek(apiUrl, token, weekStart, jobFilter ?? undefined),
        jobFilter === null
          ? null
          : fetchStaffWeek(apiUrl, token, weekStart, undefined),
      ]);
      // Belt-and-suspenders: the server narrows drafts away from
      // non-writers; if one ever leaked, a read role must still never see
      // a draft artifact (P5).
      const narrow = (list: StaffShift[]): StaffShift[] =>
        canWriteSchedule ? list : list.filter((s) => !isDraftShift(s));
      const shifts = narrow(data.shifts);
      setWeekState({ kind: 'ready', shifts });
      setWeekWideShifts(narrow((unfiltered ?? data).shifts));
    } catch (err) {
      const message = humanizeApiError(err instanceof Error ? err.message : null);
      setWeekState({ kind: 'error', message });
      // An unknown week-wide set must not keep advertising the PREVIOUS
      // week's drafts on the Publish button.
      setWeekWideShifts(null);
    }
  }, [apiUrl, weekStart, jobFilter, canWriteSchedule]);

  // #278 — Load the active staff roster for the Add-shift typeahead. The
  // `adminStaffApi.getStaff` call hits the tenant-scoped
  // GET /api/v2/admin/staff endpoint (the server resolves the caller's
  // tenant from the authenticated StaffUser — no UUID is ever supplied by
  // the client). On any failure we land in `error` so the form can render a
  // retry affordance instead of a UUID input. Always (re)fetches — both the
  // form-open path and the manual "Retry roster" button call this; gating is
  // the caller's job.
  const loadRoster = useCallback(async () => {
    setRosterState({ kind: 'loading' });
    const result = await adminStaffApi.getStaff(apiUrl);
    if (result.data?.staff) {
      setRosterState({
        kind: 'ready',
        staff: result.data.staff.filter((s) => s.is_active),
      });
    } else {
      // `null` → the render layer substitutes the translated fallback copy.
      setRosterState({ kind: 'error', message: result.error ?? null });
    }
  }, [apiUrl]);

  useEffect(() => {
    if (view === 'day') {
      void loadDay();
    } else {
      void loadWeek();
    }
  }, [view, loadDay, loadWeek]);

  // #665 PR-B — the person-row grid needs the roster for its rows (not just
  // the Add-shift picker), so Week view loads it on entry. A failure is
  // NOT silent: the grid renders from shift data alone plus an honest
  // degraded strip with a retry (#278 posture — never an empty roster that
  // reads as "no staff exist").
  useEffect(() => {
    if (view === 'week' && rosterState.kind === 'idle') {
      void loadRoster();
    }
  }, [view, rosterState.kind, loadRoster]);

  // #427 W2 — who's away, for both views. Deliberately a SEPARATE read from
  // the shift grid: a failed time-off read degrades the bands to an honest
  // "we couldn't check", it never takes the schedule down with it.
  useEffect(() => {
    void loadTimeOff();
  }, [loadTimeOff]);

  // ── Day-view derived data ──────────────────────────────────────────
  const groomingBlocks: GroomingBlock[] = useMemo(() => {
    if (dayState.kind !== 'ready') return [];
    const groomingTypeIds = new Set(
      dayState.serviceTypes
        .filter((s) => s.name === 'grooming')
        .map((s) => s.id),
    );
    const durationByTypeId = new Map<string, number>();
    for (const s of dayState.serviceTypes) {
      durationByTypeId.set(
        s.id,
        typeof s.duration_minutes === 'number' && s.duration_minutes > 0
          ? s.duration_minutes
          : DEFAULT_GROOMING_DURATION_MIN,
      );
    }
    const arrivalByBookingId = new Map<string, ScheduleDayArrival>();
    for (const a of dayState.arrivals) arrivalByBookingId.set(a.bookingId, a);
    for (const d of dayState.departures) arrivalByBookingId.set(d.bookingId, d);

    return dayState.bookings
      .filter(
        (b) =>
          groomingTypeIds.has(b.service_type_id) &&
          typeof b.start_time === 'string' &&
          /^\d{2}:\d{2}$/.test(b.start_time),
      )
      .map((b) => {
        const arrival = arrivalByBookingId.get(b.id);
        const duration =
          durationByTypeId.get(b.service_type_id) ??
          DEFAULT_GROOMING_DURATION_MIN;
        const serviceName =
          dayState.serviceTypes.find((s) => s.id === b.service_type_id)
            ?.display_name ?? 'Grooming';
        // #481 item 1 — the day-view previously resolved names ONLY via the
        // dashboard/today arrivals+departures join, which is scoped to
        // daycare/boarding traffic and drops a grooming appointment once
        // it's checked out (it stops appearing in "today"'s arrival list).
        // The Bookings page never had this bug because it reads the names
        // the schedule endpoint itself already resolves (#328 A#4:
        // `customer_name` / `dog_names` on each booking row). Prefer those
        // booking-native fields; fall back to the arrivals join (still
        // useful pre-check-in, when a booking might lack the enrichment),
        // then the generic default only if both are empty.
        const customerName =
          b.customer_name ?? arrival?.customerName ?? 'Customer';
        const dogNames =
          b.dog_names && b.dog_names.length > 0
            ? b.dog_names
            : (arrival?.dogNames ?? []);
        return {
          bookingId: b.id,
          customerName,
          dogNames,
          startTime: b.start_time as string,
          durationMinutes: duration,
          status: b.status,
          serviceName,
          isLeg: b.isLeg === true,
        };
      });
  }, [dayState]);

  // #984 — "X of Y slots filled" is a CAPACITY number. A timed add-on
  // (a nail trim at 10:00) still draws as a block — it is work in the groomer's
  // day — but it is not a booked chair: the server's slot and capacity reads
  // exclude service legs, and this count must agree with them.
  const groomingFilled = groomingBlocks.filter(
    (b) => b.status !== 'cancelled' && b.status !== 'no_show' && !b.isLeg,
  ).length;

  // #283 — denominator resolution. Rule-resolved capacity when the booking
  // engine has one for this date (0 = an honest closed day); otherwise the
  // constant fallback, which the indicator labels "(default)" so a guessed
  // denominator is never presented as configured truth (Principle 5).
  const groomingRuleCapacity =
    dayState.kind === 'ready' &&
    dayState.groomingCapacity !== null &&
    dayState.groomingCapacity.capacitySource === 'rules'
      ? dayState.groomingCapacity.capacity
      : null;
  const groomingSlotTotal = groomingRuleCapacity ?? DEFAULT_GROOMING_SLOTS;
  const groomingCapacityIsDefault = groomingRuleCapacity === null;

  const weekDates = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => ({
        iso: addIsoDays(weekStart, i),
        label: DAY_LABELS[i]!,
      })),
    [weekStart],
  );

  // ── #665 PR-B — grid rows: active roster + anyone with shifts ──────
  // Active roster people always get a row (an empty row IS the affordance:
  // click a day to schedule them). People with shifts who are no longer on
  // the active roster keep a row too — their commitments don't evaporate —
  // but get no add buttons (the server refuses shifts for them).
  const gridPeople: WeekGridPerson[] = useMemo(() => {
    const roster = rosterState.kind === 'ready' ? rosterState.staff : [];
    const active = roster.map((s) => ({
      staffId: s.id,
      name: `${s.first_name} ${s.last_name}`.trim() || s.username,
      isActive: true,
    }));
    active.sort((a, b) => a.name.localeCompare(b.name));
    const known = new Set(active.map((p) => p.staffId));
    const extras: WeekGridPerson[] = [];
    if (weekState.kind === 'ready') {
      for (const s of weekState.shifts) {
        if (
          !known.has(s.staff_user_id) &&
          !extras.some((e) => e.staffId === s.staff_user_id)
        ) {
          extras.push({
            staffId: s.staff_user_id,
            name: s.staff_name,
            isActive: false,
          });
        }
      }
    }
    extras.sort((a, b) => a.name.localeCompare(b.name));
    return [...active, ...extras];
  }, [rosterState, weekState]);

  // Chip set = job functions present in the WEEK-WIDE set, in the
  // canonical STAFF_ROLES order (unknown server-side additions last).
  // Derived (not stored) so an in-place create/edit that introduces a new
  // function grows the chip row without waiting for a refetch (review
  // minor e).
  const laneOptions = useMemo(() => {
    const present = new Set((weekWideShifts ?? []).map((s) => String(s.role)));
    const known = STAFF_ROLES.filter((r) => present.has(r));
    const unknown = [...present]
      .filter((r) => !(STAFF_ROLES as readonly string[]).includes(r))
      .sort();
    return [...known, ...unknown];
  }, [weekWideShifts]);

  /**
   * Live WEEK-WIDE draft count (the Publish button's number and the modal
   * headline). Derived from the unfiltered set — the publish action covers
   * the whole week, so a lane lens must never shrink this number (review
   * fix IMPORTANT-1). Date-bounded because a create dated outside the
   * visible week lands in the local set but NOT in the publish range.
   */
  const weekDraftCount = useMemo(() => {
    if (!weekWideShifts) return 0;
    const weekEnd = addIsoDays(weekStart, 6);
    return weekWideShifts.filter(
      (s) => isDraftShift(s) && s.date >= weekStart && s.date <= weekEnd,
    ).length;
  }, [weekWideShifts, weekStart]);

  /**
   * #665 PR-B — person-first create prefill: the person's MOST RECENT shift
   * in the loaded week (latest on-or-before the clicked day, else their
   * latest overall — When I Work's "frequent shifts" touch). No history →
   * the form's standard defaults.
   */
  const prefillFor = useCallback(
    (staffId: string, iso: string): { start: string; end: string; role: StaffRole } => {
      const mine =
        weekState.kind === 'ready'
          ? weekState.shifts
              .filter((s) => s.staff_user_id === staffId)
              .sort((a, b) =>
                `${a.date}T${a.start_time}`.localeCompare(
                  `${b.date}T${b.start_time}`,
                ),
              )
          : [];
      const onOrBefore = mine.filter((s) => s.date <= iso);
      const src = onOrBefore[onOrBefore.length - 1] ?? mine[mine.length - 1];
      if (!src) return { start: '08:00', end: '17:00', role: 'general' };
      return {
        start: src.start_time,
        end: src.end_time,
        role: (STAFF_ROLES as readonly string[]).includes(String(src.role))
          ? (src.role as StaffRole)
          : 'general',
      };
    },
    [weekState],
  );

  /**
   * #427 W2 — who is away on a given day, per APPROVED time off. Returns []
   * when the time-off read failed; the render layer shows the honest
   * "couldn't check" strip in that case rather than implying nobody is away.
   */
  const awayOn = useCallback(
    (iso: string): TimeOffRequest[] =>
      timeOffState.kind === 'ok'
        ? timeOffState.requests.filter((r) =>
            isDayWithin(iso, r.start_date, r.end_date),
          )
        : [],
    [timeOffState],
  );

  const openForm = (
    formAt?: string,
    // #665 PR-B — person-first create from a grid cell: the clicked
    // person's id + their most-recent-shift times/function.
    prefill?: { staffId: string; start: string; end: string; role: StaffRole },
  ): void => {
    setFormDate(formAt ?? weekStart);
    setFormStaffId(prefill?.staffId ?? '');
    setFormStart(prefill?.start ?? '08:00');
    setFormEnd(prefill?.end ?? '17:00');
    setFormRole(prefill?.role ?? 'general');
    setFormError(null);
    setConflict(null);
    setEditShift(null);
    setShowForm(true);
    // #278 — Refresh the roster every time the form opens (not just once per
    // mount). A roster that's still loading or already ready is left alone;
    // an idle or previously-errored roster is (re)fetched so a transient
    // failure self-heals on the next open.
    if (rosterState.kind === 'idle' || rosterState.kind === 'error') {
      void loadRoster();
    }
  };

  /**
   * #427 W2 — create the shift, with a 409 that is a DECISION POINT rather
   * than a dead end (Principle 2: errors say how to recover):
   *
   *  - `overlapping_shift` → the person is already on the floor in those
   *    hours. We name the collision and offer NO override: you cannot be in
   *    two places at once. That's physics, not policy.
   *  - `time_off_conflict` → they have APPROVED time off. That IS policy, and
   *    a manager is allowed to override it — but only as an explicit,
   *    named decision ("Schedule anyway"), never silently.
   *
   * Note this is NOT the split-shift case: two NON-overlapping shifts on one
   * day (06:00–10:00 open, 16:00–20:00 close) are legal and the server
   * accepts them; only genuine time overlap 409s.
   */
  const submitShift = async (override: boolean): Promise<void> => {
    const { token } = readSession();
    if (!token) return;
    // #278 — A valid selection means the chosen id is a real active roster
    // member. There is no raw-UUID entry path, so an unselected picker is the
    // only failure mode here.
    if (
      rosterState.kind !== 'ready' ||
      !rosterState.staff.some((s) => s.id === formStaffId)
    ) {
      setFormError(t('admin.schedule.selectStaffPrompt'));
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const created = await createStaffShift(apiUrl, token, {
        staffUserId: formStaffId,
        date: formDate,
        startTime: formStart,
        endTime: formEnd,
        role: formRole,
        ...(override ? { override: true } : {}),
        // #665 PR-B — the DRAFT contract follows the Publish button: Week
        // view has one, so its creates are drafts (publish is the explicit
        // commitment point). Day view has NO publish affordance — a draft
        // created there would be invisible in the very coverage strip it
        // was created from (the server now excludes drafts from
        // /staff-on-duty) with no way to publish it without discovering
        // Week view. Day-view creates therefore publish immediately — the
        // legacy create contract, which since PR-A also stamps notified_at
        // (silent counts as announced), so a later edit still
        // delta-notifies.
        publishImmediately: view === 'day',
      });
      setWeekState((curr) =>
        curr.kind === 'ready'
          ? { kind: 'ready', shifts: [...curr.shifts, created.shift] }
          : curr,
      );
      // Keep the week-wide set (Publish count, lane chips) in step with
      // the in-place append (review minor e).
      setWeekWideShifts((curr) => (curr ? [...curr, created.shift] : curr));
      setConflict(null);
      setShowForm(false);
      // #665 — a shift saved from Day view must show up in the coverage
      // strip it was created from, not only over in Week view — that is
      // WHY it publishes immediately (drafts are excluded server-side).
      // Reload the day read (staff-on-duty); if the manager dated the
      // shift for a different day, the refreshed strip honestly keeps not
      // showing it.
      if (view === 'day') {
        void loadDay();
      }
    } catch (err) {
      if (err instanceof ShiftConflictError) {
        const c = err.conflict;
        if (c.kind === 'overlapping_shift') {
          const collisionId = c.conflictingShiftId;
          const collision =
            weekState.kind === 'ready'
              ? weekState.shifts.find((s) => s.id === collisionId)
              : undefined;
          setConflict({
            kind: 'overlapping_shift',
            message: collision
              ? `${collision.staff_name} already works ${formatTimeRange(
                  collision.start_time,
                  collision.end_time,
                )} that day. Change the hours, or remove the other shift first.`
              : `${err.message} Change the hours, or remove the other shift first.`,
          });
        } else {
          const name =
            rosterState.kind === 'ready'
              ? rosterState.staff.find((s) => s.id === formStaffId)
              : undefined;
          const first = name
            ? `${name.first_name} ${name.last_name}`.trim() || name.username
            : 'This person';
          setConflict({
            kind: 'time_off_conflict',
            message: `${first} has approved time off that day. Schedule anyway?`,
          });
        }
        return;
      }
      setFormError(err instanceof Error ? err.message : 'Failed to create shift');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    await submitShift(false);
  };

  /**
   * #427 W2 — "Copy last week". The server does the work and reports back
   * `{ created, skipped[] }`. A copy that skipped people is NOT a clean
   * success: we say how many landed AND name everyone who didn't, with the
   * reason (Principle 5). Silence here would mean a planner walks away
   * believing Dezirea is covered when she isn't.
   */
  const handleCopyWeek = async (): Promise<void> => {
    const { token } = readSession();
    if (!token) return;
    setCopyState({ kind: 'copying' });
    try {
      const result = await copyStaffWeek(apiUrl, token, {
        fromStartDate: addIsoDays(weekStart, -7),
        toStartDate: weekStart,
      });
      setCopyState({
        kind: 'done',
        created: result.created?.length ?? 0,
        skipped: result.skipped ?? [],
      });
      await loadWeek();
    } catch (err) {
      setCopyState({
        kind: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'We couldn’t copy last week. Try again.',
      });
    }
  };

  const handleDelete = async (shift: StaffShift): Promise<void> => {
    const { token } = readSession();
    if (!token || weekState.kind !== 'ready') return;
    setActionError(null);
    const previous = weekState.shifts;
    const previousWeekWide = weekWideShifts;
    setWeekState({
      kind: 'ready',
      shifts: previous.filter((s) => s.id !== shift.id),
    });
    setWeekWideShifts((curr) =>
      curr ? curr.filter((s) => s.id !== shift.id) : curr,
    );
    try {
      await deleteStaffShift(apiUrl, token, shift.id);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to delete shift',
      );
      setWeekState({ kind: 'ready', shifts: previous });
      setWeekWideShifts(previousWeekWide);
    }
  };

  // ── #665 PR-B — edit-sheet + publish handlers ──────────────────────

  /** The sheet's PATCH answer replaces the row in place — the grid shows
   *  the SERVER's version of the shift, not the client's hope. */
  const handleShiftSaved = (updated: StaffShift): void => {
    setWeekState((curr) =>
      curr.kind === 'ready'
        ? {
            kind: 'ready',
            shifts: curr.shifts.map((s) => (s.id === updated.id ? updated : s)),
          }
        : curr,
    );
    // The week-wide set backs the Publish count + lane chips — an edited
    // job function must grow/shrink the chip row without a refetch
    // (review minor e).
    setWeekWideShifts((curr) =>
      curr ? curr.map((s) => (s.id === updated.id ? updated : s)) : curr,
    );
    setEditShift(null);
  };

  /** Delete from the sheet: close, remove optimistically, restore + say so
   *  on failure (a silently resurrecting shift would be worse than the
   *  error). */
  const handleSheetDelete = (shift: StaffShift): void => {
    setEditShift(null);
    void handleDelete(shift);
  };

  const handlePublish = async (mode: PublishNotifyMode): Promise<void> => {
    const { token } = readSession();
    if (!token || !publishTarget) return;
    setPublishing(true);
    setPublishError(null);
    try {
      // The FROZEN range from modal-open — never live weekStart, which the
      // user may have navigated behind the modal (review minor a).
      const result = await publishStaffSchedule(apiUrl, token, {
        from: publishTarget.from,
        to: publishTarget.to,
        notify: mode,
      });
      setPublishTarget(null);
      // Server-confirmed numbers, never the client's guess (P5).
      setPublishResult({
        published: result.published_count,
        notified: result.notified.length,
      });
      await loadWeek();
    } catch (err) {
      setPublishError(
        err instanceof Error
          ? err.message
          : 'We couldn’t publish the schedule. Try again.',
      );
    } finally {
      setPublishing(false);
    }
  };

  // #278 — Submit is only enabled once a real roster member is selected.
  const canSubmit =
    !submitting &&
    rosterState.kind === 'ready' &&
    rosterState.staff.some((s) => s.id === formStaffId);

  // #665 — ONE Add-shift form, shared by BOTH views. It was born inside
  // the Week branch, which left Day view (the default landing) with no
  // affordance at all — the discoverability defect this issue is about.
  // Declared once here so both views render the identical form: same
  // roster state machine (#278 — error + retry, never a silent hide),
  // same honest empty state, same conflict handling, same submit path.
  // #712 — belt-and-suspenders: the form itself is also write-gated, so a
  // read-only role can never reach it even if a future opener forgets the
  // affordance gate.
  const addShiftForm = canWriteSchedule && showForm ? (
            <form
              className="ferro-form"
              onSubmit={handleSubmit}
              aria-label="Add shift"
            >
              {/* #278 — Staff selection is a name-keyed <select> over the
                    active roster (the same GET /api/v2/admin/staff read the
                    Staff page uses). The option VALUE carries the staff id;
                    the planner only ever sees names — a UUID is never
                    user-visible, and there is no raw-UUID fallback in any
                    state. This wrapper is a <div> (not <label>) because the
                    error/empty states nest interactive buttons/links, which
                    a <label> must not wrap; the selects carry their
                    accessible name via aria-label. */}
              <div className="ferro-field">
                <span className="ferro-field__label">
                  {t('admin.schedule.staffMember')}
                </span>
                {rosterState.kind === 'loading' ||
                rosterState.kind === 'idle' ? (
                  <select
                    className="ferro-field__input"
                    disabled
                    aria-label={t('admin.schedule.staffMember')}
                    aria-busy="true"
                    data-testid="staff-select-loading"
                  >
                    <option>{t('admin.schedule.loadingStaff')}</option>
                  </select>
                ) : null}

                {rosterState.kind === 'error' ? (
                  <div className="ferro-dashboard__error" role="alert">
                    <p>
                      {rosterState.message ??
                        t('admin.schedule.rosterLoadError')}
                    </p>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      onClick={() => void loadRoster()}
                    >
                      {t('admin.schedule.retryRoster')}
                    </button>
                  </div>
                ) : null}

                {rosterState.kind === 'ready' &&
                rosterState.staff.length === 0 ? (
                  <div className="ferro-empty-card" data-testid="roster-empty">
                    <p>{t('admin.schedule.noStaffYet')}</p>
                    <Link className="ferro-btn ferro-btn--ghost" to="/staff">
                      {t('admin.schedule.inviteYourTeam')}
                    </Link>
                  </div>
                ) : null}

                {rosterState.kind === 'ready' &&
                rosterState.staff.length > 0 ? (
                  <select
                    className="ferro-field__input"
                    value={formStaffId}
                    onChange={(e) => setFormStaffId(e.target.value)}
                    required
                    aria-label={t('admin.schedule.staffMember')}
                    data-testid="staff-select"
                  >
                    <option value="" disabled>
                      {t('admin.schedule.selectStaff')}
                    </option>
                    {rosterState.staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {staffLabel(s)}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
              <label className="ferro-field">
                <span className="ferro-field__label">Date</span>
                <input
                  type="date"
                  className="ferro-field__input"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  required
                />
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Start</span>
                <input
                  type="time"
                  className="ferro-field__input"
                  value={formStart}
                  onChange={(e) => setFormStart(e.target.value)}
                  required
                />
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">End</span>
                <input
                  type="time"
                  className="ferro-field__input"
                  value={formEnd}
                  onChange={(e) => setFormEnd(e.target.value)}
                  required
                />
              </label>
              {/* #427 W2 — the JOB FUNCTION for this shift: what this person
                    is DOING for these hours. It is not, and never implies,
                    their login role — Jorge can log in as a groomer and work
                    the front desk on Saturday morning. All six functions are
                    offered; the options show labels, never the enum. */}
              <label className="ferro-field">
                <span className="ferro-field__label">Job for this shift</span>
                <select
                  className="ferro-field__input"
                  value={formRole}
                  onChange={(e) => setFormRole(e.target.value as StaffRole)}
                  data-testid="shift-role-select"
                >
                  {STAFF_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {STAFF_ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </label>

              {formError ? (
                <div className="ferro-dashboard__error" role="alert">
                  <p>{formError}</p>
                </div>
              ) : null}

              {/* #427 W2 — an overlapping shift is a hard stop: no override
                    is offered, because a person cannot be in two places at
                    once. The copy names the collision and says how to fix it. */}
              {conflict?.kind === 'overlapping_shift' ? (
                <div
                  className="ferro-alert ferro-alert--error"
                  role="alert"
                  data-testid="shift-overlap-error"
                >
                  {conflict.message}
                </div>
              ) : null}

              {/* #427 W2 — approved time off is POLICY, and a manager may
                    knowingly override it. That is a DECISION, so it gets an
                    explicit affordance and is never silent. */}
              {conflict?.kind === 'time_off_conflict' ? (
                <div
                  className="ferro-alert ferro-alert--warning"
                  role="alert"
                  data-testid="shift-timeoff-conflict"
                >
                  <p>{conflict.message}</p>
                  <div className="ferro-form__actions">
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--outline"
                      disabled={submitting}
                      onClick={() => void submitShift(true)}
                      data-testid="shift-timeoff-override"
                    >
                      Schedule anyway
                    </button>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      onClick={() => setConflict(null)}
                    >
                      Pick another day
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="ferro-form__actions">
                <button
                  type="submit"
                  className="ferro-btn ferro-btn--primary"
                  disabled={!canSubmit}
                >
                  {submitting ? 'Saving…' : 'Save shift'}
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => {
                    setShowForm(false);
                    setConflict(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
  ) : null;

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <section
      className="ferro-admin-page"
      aria-busy={
        view === 'day'
          ? dayState.kind === 'loading'
          : weekState.kind === 'loading'
      }
    >
      <header className="ferro-toolbar">
        <h1 className="ferro-section__title" style={{ marginBottom: 0 }}>
          Schedule
        </h1>
        <div
          className="ferro-schedule-view-toggle"
          role="group"
          aria-label="Schedule view"
        >
          <button
            type="button"
            className={`ferro-schedule-view-toggle__btn${
              view === 'day' ? ' ferro-schedule-view-toggle__btn--active' : ''
            }`}
            onClick={() => setView('day')}
            aria-pressed={view === 'day'}
          >
            Day
          </button>
          <button
            type="button"
            className={`ferro-schedule-view-toggle__btn${
              view === 'week' ? ' ferro-schedule-view-toggle__btn--active' : ''
            }`}
            onClick={() => setView('week')}
            aria-pressed={view === 'week'}
          >
            Week
          </button>
        </div>
      </header>

      {view === 'day' ? (
        <>
          <form
            className="ferro-toolbar"
            onSubmit={(e) => e.preventDefault()}
            aria-label="Day controls"
          >
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setDate(addIsoDays(date, -1))}
              aria-label="Previous day"
            >
              ← Prev
            </button>
            <label className="ferro-field" style={{ margin: 0 }}>
              <span className="ferro-field__label" style={{ display: 'none' }}>
                Date
              </span>
              <input
                type="date"
                className="ferro-field__input"
                value={date}
                onChange={(e) => setDate(e.target.value || todayIsoDate())}
                aria-label="Selected date"
              />
            </label>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setDate(addIsoDays(date, 1))}
              aria-label="Next day"
            >
              Next →
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setDate(todayIsoDate())}
            >
              Today
            </button>
            <span className="ferro-muted">{formatLongDate(date)}</span>
            {/* #665 — shift creation must be reachable from the view users
                actually land on. This is Day view's one filled-primary
                (DESIGN-PHILOSOPHY craft standard); the form opens pre-dated
                to the day being looked at.
                #712 — write-gated: a receptionist (read:all only) never sees
                a button whose POST the server 403s. */}
            {canWriteSchedule ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                onClick={() => openForm(date)}
                data-testid="day-add-shift"
              >
                {t('admin.schedule.addShift')}
              </button>
            ) : null}
          </form>

          {addShiftForm}

          {dayState.kind === 'loading' ? (
            <p className="ferro-muted" role="status">
              Loading schedule…
            </p>
          ) : null}

          {dayState.kind === 'error' ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>{dayState.message}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => void loadDay()}
              >
                Retry
              </button>
            </div>
          ) : null}

          {dayState.kind === 'ready' ? (
            <>
              <div
                className="ferro-schedule-capacity"
                data-testid="grooming-capacity"
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {t('admin.schedule.groomingCapacity')}
                  </div>
                  <div className="ferro-muted">
                    {groomingSlotTotal === 0
                      ? t('admin.schedule.groomingClosedToday')
                      : t(
                          groomingCapacityIsDefault
                            ? 'admin.schedule.slotsFilledDefault'
                            : 'admin.schedule.slotsFilled',
                          {
                            filled: groomingFilled,
                            capacity: groomingSlotTotal,
                          },
                        )}
                  </div>
                </div>
                <div className="ferro-schedule-capacity__bar-wrap">
                  <div
                    className={`ferro-schedule-capacity__bar ${
                      groomingSlotTotal === 0
                        ? groomingFilled > 0
                          ? 'ferro-schedule-capacity__bar--full'
                          : ''
                        : capacityBarClass(groomingFilled, groomingSlotTotal)
                    }`}
                    style={{
                      width: `${
                        groomingSlotTotal > 0
                          ? Math.min(
                              100,
                              (groomingFilled / groomingSlotTotal) * 100,
                            )
                          : groomingFilled > 0
                            ? 100
                            : 0
                      }%`,
                    }}
                  />
                </div>
              </div>

              <section className="ferro-schedule-section" aria-label="Staff coverage">
                <h2 className="ferro-schedule-section__title">Staff coverage</h2>

                {/* #427 W2 — who is away TODAY, above the coverage strip.
                    An honest "couldn't check" beats an empty strip that
                    implies everyone is available (Principle 5). */}
                {timeOffState.kind === 'unavailable' ? (
                  <div
                    className="ferro-alert ferro-alert--warning"
                    role="status"
                    data-testid="time-off-unavailable-day"
                  >
                    We couldn’t check who has time off today.
                  </div>
                ) : awayOn(date).length > 0 ? (
                  <div className="ferro-timeoff-strip" data-testid="day-timeoff-strip">
                    {awayOn(date).map((r) => (
                      <span
                        key={r.id}
                        className="ferro-timeoff-band ferro-timeoff-band--approved"
                      >
                        {r.staff_name} — {timeOffTypeLabel(r.type)}
                      </span>
                    ))}
                  </div>
                ) : null}

                {dayState.shifts.length === 0 ? (
                  // #665 — the empty state that convinced the owner the
                  // feature didn't exist. Empty states carry teaching copy
                  // + an action (five mandatory component states; Principle
                  // 2: the next action is always obvious) — never a dead
                  // sentence.
                  // #712 — for WRITE roles. A read-only role keeps the honest
                  // sentence ("No staff scheduled today.") WITHOUT the CTA:
                  // an action button whose POST 403s is worse than no button
                  // (Principle 5 — never render an affordance the server
                  // will refuse).
                  <div
                    className="ferro-empty-card"
                    data-testid="day-coverage-empty"
                  >
                    <p>{t('admin.schedule.noStaffScheduledToday')}</p>
                    {canWriteSchedule ? (
                      <div className="ferro-form__actions">
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--outline"
                          onClick={() => openForm(date)}
                          data-testid="day-empty-add-shift"
                        >
                          {t('admin.schedule.addAShift')}
                        </button>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--ghost"
                          onClick={() => setView('week')}
                          data-testid="day-empty-plan-week"
                        >
                          {t('admin.schedule.planWholeWeek')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  // #665 — the strip's window derives from the day's actual
                  // shifts (coverageWindow), so a 20:00–23:00 overnight-
                  // function shift renders as a real bar instead of being
                  // clipped by the old hardcoded 8–18 clamp.
                  <div data-testid="staff-coverage">
                    {(() => {
                      const win = coverageWindow(dayState.shifts);
                      return dayState.shifts.map((s) => (
                        <StaffShiftRow
                          key={s.shiftId}
                          name={s.name}
                          role={s.role}
                          shiftStart={s.shiftStart}
                          shiftEnd={s.shiftEnd}
                          dayStartHour={win.startHour}
                          dayEndHour={win.endHour}
                        />
                      ));
                    })()}
                  </div>
                )}
              </section>

              <section className="ferro-schedule-section" aria-label="Grooming calendar">
                <h2 className="ferro-schedule-section__title">
                  Grooming appointments
                </h2>
                <CalendarGrid
                  blocks={groomingBlocks}
                  dayStartHour={DAY_START_HOUR}
                  dayEndHour={DAY_END_HOUR}
                />
              </section>

              <section className="ferro-schedule-section" aria-label="Arrivals">
                {/* #984 — the count is VISITS. A service leg is its own row on
                    /today, so `.length` read Coco arriving once with a nail
                    trim as two arrivals. The leg card still renders, tagged. */}
                <h2 className="ferro-schedule-section__title">
                  Arrivals ({countVisits(dayState.arrivals)})
                </h2>
                {dayState.arrivals.length === 0 ? (
                  <p className="ferro-muted">No arrivals scheduled.</p>
                ) : (
                  <ul className="ferro-arrival-list" aria-label="Arrivals">
                    {dayState.arrivals.map((a) => (
                      <li
                        key={a.bookingId}
                        className="ferro-arrival-card"
                        data-testid={`schedule-arrival-${a.bookingId}`}
                      >
                        <span className="ferro-arrival-card__time">
                          {formatExpectedTime(a.expectedTime)} ·{' '}
                          {a.isLeg === true ? (a.addOnName ?? a.serviceType) : a.serviceType}
                        </span>
                        {a.isLeg === true ? (
                          <span
                            className="ferro-arrival-card__leg"
                            data-testid={`schedule-leg-tag-${a.bookingId}`}
                          >
                            Added service on this visit
                          </span>
                        ) : null}
                        <span className="ferro-arrival-card__owner">
                          {a.customerName}
                        </span>
                        <span className="ferro-arrival-card__dogs">
                          {a.dogNames.join(', ') || 'No dogs'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="ferro-schedule-section" aria-label="Departures">
                <h2 className="ferro-schedule-section__title">
                  Departures ({countVisits(dayState.departures)})
                </h2>
                {dayState.departures.length === 0 ? (
                  <p className="ferro-muted">No departures scheduled.</p>
                ) : (
                  <ul className="ferro-arrival-list" aria-label="Departures">
                    {dayState.departures.map((d) => (
                      <li
                        key={d.bookingId}
                        className="ferro-arrival-card"
                        data-testid={`schedule-departure-${d.bookingId}`}
                      >
                        <span className="ferro-arrival-card__time">
                          {formatExpectedTime(d.expectedTime)} ·{' '}
                          {d.isLeg === true ? (d.addOnName ?? d.serviceType) : d.serviceType}
                        </span>
                        {d.isLeg === true ? (
                          <span
                            className="ferro-arrival-card__leg"
                            data-testid={`schedule-leg-tag-${d.bookingId}`}
                          >
                            Added service on this visit
                          </span>
                        ) : null}
                        <span className="ferro-arrival-card__owner">
                          {d.customerName}
                        </span>
                        <span className="ferro-arrival-card__dogs">
                          {d.dogNames.join(', ') || 'No dogs'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          ) : null}
        </>
      ) : (
        <>
          <form
            className="ferro-toolbar"
            onSubmit={(e) => e.preventDefault()}
            aria-label="Week controls"
          >
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setWeekStart(addIsoDays(weekStart, -7))}
            >
              ← Prev
            </button>
            <span className="ferro-muted">
              {/* Human dates, never raw ISO (Design Philosophy anti-pattern
                  list) — same formatter as MySchedule's week label. */}
              {formatDayRange(weekStart, addIsoDays(weekStart, 6))}
            </span>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setWeekStart(addIsoDays(weekStart, 7))}
            >
              Next →
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setWeekStart(mondayIsoDate())}
            >
              This week
            </button>
            {/* #665 PR-B — PUBLISH is this view's one filled-primary
                (Deputy's grey→green: committing the week to staff is the
                view's defining action; a second primary is an auto-reject).
                Add shift and Copy last week demote to outline.
                #712 — all three are write-gated (each backs a
                staff-schedule:write:all server route); a read-only role
                keeps the grid without the dead buttons. */}
            {canWriteSchedule ? (
              <>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--outline"
                  onClick={() => void handleCopyWeek()}
                  disabled={copyState.kind === 'copying'}
                  aria-busy={copyState.kind === 'copying'}
                  data-testid="copy-week"
                >
                  {copyState.kind === 'copying' ? 'Copying…' : 'Copy last week'}
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--outline"
                  onClick={() => openForm()}
                >
                  {t('admin.schedule.addShift')}
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--primary"
                  onClick={() => {
                    setPublishError(null);
                    // Freeze the range + headline count at open (minor a).
                    setPublishTarget({
                      from: weekStart,
                      to: addIsoDays(weekStart, 6),
                      draftCount: weekDraftCount,
                    });
                  }}
                  disabled={weekDraftCount === 0}
                  title={
                    weekDraftCount === 0
                      ? 'No draft shifts to publish'
                      : undefined
                  }
                  data-testid="publish-week"
                >
                  Publish{weekDraftCount > 0 ? ` (${weekDraftCount})` : ''}
                </button>
              </>
            ) : null}
          </form>

          {/* #665 PR-B — the publish outcome, server-confirmed (P5). */}
          {publishResult ? (
            <div
              className="ferro-alert ferro-alert--success"
              role="status"
              data-testid="publish-result"
            >
              <p>
                Published {publishResult.published}{' '}
                {publishResult.published === 1 ? 'shift' : 'shifts'}
                {publishResult.notified > 0
                  ? ` · notified ${publishResult.notified} ${
                      publishResult.notified === 1 ? 'person' : 'people'
                    }`
                  : ' · nobody was notified'}
                .
              </p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => setPublishResult(null)}
              >
                Dismiss
              </button>
            </div>
          ) : null}

          {/* #665 PR-B — layer chips: ONE grid, N lenses (the 2026-06-01
              "layered calendars" decision resolved as filters). Only offered
              when the week actually has lanes to filter. Reading through a
              lens is a READ — receptionists keep it. */}
          {laneOptions.length > 0 ? (
            <div
              className="ferro-layer-chips"
              role="group"
              aria-label="Filter by job function"
              data-testid="layer-chips"
            >
              <button
                type="button"
                className="ferro-layer-chip"
                aria-pressed={jobFilter === null}
                onClick={() => setJobFilter(null)}
              >
                All
              </button>
              {laneOptions.map((fn) => (
                <button
                  key={fn}
                  type="button"
                  className="ferro-layer-chip"
                  aria-pressed={jobFilter === fn}
                  onClick={() =>
                    setJobFilter(
                      jobFilter === fn ? null : (fn as StaffRole),
                    )
                  }
                >
                  {jobFunctionLabel(fn)}
                </button>
              ))}
            </div>
          ) : null}

          {/* #427 W2 — the honest copy result. A partial copy renders as a
              WARNING that names every skipped person and why; only a copy
              with zero skips gets to look like a clean success (Principle 5). */}
          {copyState.kind === 'done' ? (
            <div
              className={`ferro-alert ferro-alert--${
                copyState.skipped.length > 0 ? 'warning' : 'success'
              }`}
              role="status"
              data-testid="copy-week-result"
            >
              <p>
                Copied {copyState.created}{' '}
                {copyState.created === 1 ? 'shift' : 'shifts'} from last week.
                {copyState.skipped.length > 0
                  ? ` Skipped ${formatSkips(copyState.skipped)}.`
                  : ''}
              </p>
              {copyState.skipped.length > 0 ? (
                <p className="ferro-muted">
                  Those days are still uncovered — add them by hand if you need
                  them.
                </p>
              ) : null}
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => setCopyState({ kind: 'idle' })}
              >
                Dismiss
              </button>
            </div>
          ) : null}

          {copyState.kind === 'error' ? (
            <div className="ferro-alert ferro-alert--error" role="alert">
              <p>{copyState.message}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => void handleCopyWeek()}
              >
                Try again
              </button>
            </div>
          ) : null}

          {timeOffState.kind === 'unavailable' ? (
            <div
              className="ferro-alert ferro-alert--warning"
              role="status"
              data-testid="time-off-unavailable"
            >
              We couldn’t check who has time off. Confirm before you schedule
              anyone — this grid can’t show absences right now.
            </div>
          ) : null}

          {addShiftForm}

          {actionError ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>{actionError}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => setActionError(null)}
              >
                Dismiss
              </button>
            </div>
          ) : null}

          {weekState.kind === 'loading' ? (
            <p className="ferro-muted" role="status">
              Loading schedule…
            </p>
          ) : null}

          {weekState.kind === 'error' ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>{weekState.message}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => void loadWeek()}
              >
                Retry
              </button>
            </div>
          ) : null}

          {/* #665 PR-B — the edit sheet (times, job function, notes, PERSON,
              delete) for the chip being edited. Same inline-panel posture as
              the Add-shift form. */}
          {editShift && canWriteSchedule ? (
            <ShiftEditSheet
              key={editShift.id}
              apiUrl={apiUrl}
              shift={editShift}
              roster={
                rosterState.kind === 'ready' ? rosterState.staff : null
              }
              onClose={() => setEditShift(null)}
              onSaved={handleShiftSaved}
              onDelete={handleSheetDelete}
            />
          ) : null}

          {/* #278/#665 — the grid renders even when the roster read failed
              (rows derive from shift data alone), but says so out loud:
              missing roster rows must never read as "these people don't
              exist". */}
          {weekState.kind === 'ready' && rosterState.kind === 'error' ? (
            <div
              className="ferro-alert ferro-alert--warning"
              role="status"
              data-testid="grid-roster-degraded"
            >
              <p>
                We couldn’t load the staff roster — showing only people who
                already have shifts this week.
              </p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => void loadRoster()}
              >
                Retry roster
              </button>
            </div>
          ) : null}

          {weekState.kind === 'ready' ? (
            gridPeople.length === 0 ? (
              canWriteSchedule && rosterState.kind === 'ready' ? (
                // Nobody to schedule: teach the first step (five mandatory
                // component states — empty = copy + action, never a dead
                // sentence).
                <div className="ferro-empty-card" data-testid="grid-empty">
                  <p>{t('admin.schedule.noStaffYet')}</p>
                  <Link className="ferro-btn ferro-btn--ghost" to="/staff">
                    {t('admin.schedule.inviteYourTeam')}
                  </Link>
                </div>
              ) : (
                <p className="ferro-muted">
                  No shifts scheduled for this week.
                </p>
              )
            ) : (
              // #665 PR-B — THE master person-row grid (spec §3): rows =
              // people, columns = Mon–Sun, cells = chips. Split shifts are
              // legal — a cell holds several chips, each with its own job
              // function. Time-off bands render IN the person×day cell so
              // the absence sits exactly where the planner would click.
              <WeekGrid
                weekDates={weekDates}
                people={gridPeople}
                shifts={weekState.shifts}
                timeOff={
                  timeOffState.kind === 'ok' ? timeOffState.requests : []
                }
                canWrite={canWriteSchedule}
                onAddShift={(staffId, iso) =>
                  openForm(iso, { staffId, ...prefillFor(staffId, iso) })
                }
                onEditShift={(shift) => {
                  setShowForm(false);
                  setEditShift(shift);
                }}
              />
            )
          ) : null}

          {/* #665 PR-B — the publish commitment point. Count + label come
              from the FROZEN modal-open snapshot, so what the modal names
              is exactly what confirm publishes (review minor a). */}
          {publishTarget && canWriteSchedule ? (
            <PublishModal
              draftCount={publishTarget.draftCount}
              weekLabel={formatDayRange(publishTarget.from, publishTarget.to)}
              laneFiltered={jobFilter !== null}
              publishing={publishing}
              error={publishError}
              onConfirm={(mode) => void handlePublish(mode)}
              onClose={() => setPublishTarget(null)}
            />
          ) : null}
        </>
      )}

      {/* #427 W2 — Time off lives on the planner's screen: managers decide
          here (approve/deny queue, single-source badge), and every desk role
          can ask for their own day off from the same place. Floor staff get
          the same form on /my-schedule, where they'll actually look. */}
      {canManageTimeOff ? <TimeOffQueue apiUrl={apiUrl} /> : null}

      <section className="ferro-schedule-section" aria-label="Request time off">
        <h2 className="ferro-schedule-section__title">Need a day off?</h2>
        {showTimeOffForm ? (
          <TimeOffRequestForm
            apiUrl={apiUrl}
            onCancel={() => setShowTimeOffForm(false)}
          />
        ) : (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setShowTimeOffForm(true)}
            data-testid="open-time-off-form"
          >
            Request time off
          </button>
        )}
      </section>
    </section>
  );
}
